// src/pages/VendorPage.jsx
import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { vendorApi } from '../../services/inventoryVendorApi';
import backendApi from '../../services/backendApi';
import ToastNotification from '../../pages/dashboard-pages/simulation/ToastNotification';
import {
  Users,
  Plus,
  Search,
  Filter,
  Edit,
  Trash2,
  Phone,
  Mail,
  MapPin,
  Star,
  Calendar,
  FileText,
  CheckCircle,
  AlertCircle,
  Clock,
  IndianRupee,
  RefreshCw,
  X,
  ChevronDown,
  ArrowRight,
  Upload,
  Download
} from 'lucide-react';
import './VendorPage.css';
import '../../styles/np-theme.css';

// Aadhaar is a national ID: the table shows only the last four digits (the edit form still has the full value).
const maskAadhar = (value) => {
  const digits = String(value || '').replace(/\D/g, '');
  if (!digits) return '';
  return digits.length <= 4 ? digits : `XXXX XXXX ${digits.slice(-4)}`;
};

const VendorPage = () => {
  const { site } = useParams();
  const resolvedSiteCode = useMemo(() => {
    const fromRoute = String(site || '').trim();
    if (fromRoute && fromRoute.toLowerCase() !== 'undefined') return fromRoute.toUpperCase();

    const fromSession = String(sessionStorage.getItem('tenant') || '').trim();
    if (fromSession) return fromSession.toUpperCase();

    const fromLocal = String(localStorage.getItem('currentSite') || '').trim();
    if (fromLocal) return fromLocal.toUpperCase();

    return 'ADMIN';
  }, [site]);
  const [searchTerm, setSearchTerm] = useState('');
  const [invoiceFilter, setInvoiceFilter] = useState('all');
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingVendor, setEditingVendor] = useState(null);
  const [vendors, setVendors] = useState([]);
  const [expandedRow, setExpandedRow] = useState(null);
  const [siteName, setSiteName] = useState('');
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState({
    show: false,
    type: 'success',
    title: '',
    message: ''
  });
  const [vendorStats, setVendorStats] = useState({
    total: 0,
    active: 0,
    pendingRenewal: 0,
    inactive: 0,
    totalValue: 0,
    totalInvoicedValue: 0
  });
  const [isRealTime, setIsRealTime] = useState(true);
  const [formErrors, setFormErrors] = useState({});
  const [modalError, setModalError] = useState('');
  const intervalRef = useRef(null);
  const POLL_INTERVAL = 30000; // 30 seconds

  const defaultColumnWidths = useMemo(
    () => [
      120, // Actions (moved to first)
      220, // Name
      150, // Location
      120, // PAN
      140, // Aadhar
      120, // TAN
      150, // GST
      180, // Bank Name
      180, // Ac No
      120, // Vendor Code
      120, // PO Number
      150, // PO Value
      180, // Payment Schedule
      200, // Product and Service
      220, // Contract
      120, // User
      180, // Value
      150, // Invoiced Value
      150, // PO Balance Amount
      300  // Remarks
    ],
    []
  );

  const [columnWidths, setColumnWidths] = useState(() => {
    const saved = localStorage.getItem('vendor-column-widths-v2');
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        if (Array.isArray(parsed) && parsed.length === defaultColumnWidths.length) {
          return parsed.map(w => Math.max(50, w));
        }
      } catch (e) {
        console.error('Error parsing saved vendor column widths:', e);
      }
    }
    return defaultColumnWidths;
  });

  const [showExportDropdown, setShowExportDropdown] = useState(false);
  const resizeRef = useRef(null);
  const fileInputRef = useRef(null);
  const invoiceFileInputRef = useRef(null);

  const [showUploadModal, setShowUploadModal] = useState(false);

  // Invoice Modal State
  const [showInvoiceModal, setShowInvoiceModal] = useState(false);
  const [selectedVendorForInvoice, setSelectedVendorForInvoice] = useState(null);
  const [newInvoice, setNewInvoice] = useState({
    invoiceDate: '',
    invoiceNumber: '',
    invoiceValue: '',
    gstAmount: '',
    totalInvoiceValue: '',
    poNumber: '',
    invoiceDocument: null
  });

  useEffect(() => {
    localStorage.setItem('vendor-column-widths-v2', JSON.stringify(columnWidths));
  }, [columnWidths]);

  // Custom date formatter for "03Nov26" format
  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return dateString;

    const day = String(date.getDate()).padStart(2, '0');
    const month = date.toLocaleString('en-IN', { month: 'short' });
    const year = String(date.getFullYear()).slice(-2);

    return `${day}${month}${year}`;
  };

  const handleResizeStart = useCallback(
    (columnIndex, e) => {
      if (columnIndex === 0) return;
      e.preventDefault();
      e.stopPropagation();
      const startX = e.clientX;
      const startWidth = columnWidths[columnIndex];
      resizeRef.current = { columnIndex, startX, startWidth };
      document.body.style.cursor = 'col-resize';
      document.body.style.userSelect = 'none';
    },
    [columnWidths]
  );

  useEffect(() => {
    const handleMouseMove = (e) => {
      if (!resizeRef.current) return;
      const { columnIndex, startX, startWidth } = resizeRef.current;
      const diff = e.clientX - startX;
      const newWidth = Math.max(50, startWidth + diff);
      setColumnWidths((prev) => {
        const next = [...prev];
        next[columnIndex] = newWidth;
        return next;
      });
    };

    const handleMouseUp = () => {
      if (resizeRef.current) {
        resizeRef.current = null;
        document.body.style.cursor = '';
        document.body.style.userSelect = '';
      }
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, []);

  const getHeaderStyle = useCallback(
    (columnIndex) => {
      const width = Math.max(50, columnWidths[columnIndex] || defaultColumnWidths[columnIndex]);
      return {
        width: `${width}px`,
        minWidth: `${width}px`,
        maxWidth: `${width}px`,
      };
    },
    [columnWidths, defaultColumnWidths]
  );

  const showToast = (type, title, message) => {
    setToast({ show: true, type, title, message });
  };

  // Fetch vendor data
  const fetchVendors = async (showLoading = true) => {
    try {
      if (showLoading) setLoading(true);

      const params = {};
      if (searchTerm) params.search = searchTerm;

      const response = await vendorApi.getAll(resolvedSiteCode, params);
      if (response.ok && response.data) {
        // Handle paginated response or direct array
        let vendorData = [];
        if (Array.isArray(response.data)) {
          vendorData = response.data;
        } else if (response.data && typeof response.data === 'object' && 'results' in response.data) {
          vendorData = Array.isArray(response.data.results) ? response.data.results : [];
        }
        setVendors(vendorData);
      } else {
        console.error('Failed to fetch vendors:', response.error);
        setVendors([]);
      }

      // Fetch stats
      const statsResponse = await vendorApi.getStats(resolvedSiteCode);
      if (statsResponse.ok && statsResponse.data) {
        setVendorStats({
          total: statsResponse.data.total || 0,
          active: statsResponse.data.active || 0,
          pendingRenewal: statsResponse.data.pendingRenewal || 0,
          inactive: statsResponse.data.inactive || 0,
          totalValue: statsResponse.data.totalValue || 0,
          totalInvoicedValue: statsResponse.data.totalInvoicedValue || 0
        });
      }
    } catch (error) {
      console.error('Error fetching vendors:', error);
    } finally {
      if (showLoading) setLoading(false);
    }
  };

  // Initial load
  useEffect(() => {
    fetchVendors();
  }, [resolvedSiteCode]);

  // Real-time polling
  useEffect(() => {
    if (isRealTime) {
      intervalRef.current = setInterval(() => {
        if (typeof document !== 'undefined' && document.visibilityState !== 'visible') {
          return;
        }
        fetchVendors(false);
      }, POLL_INTERVAL);
    } else {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    }

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [isRealTime, searchTerm, resolvedSiteCode]);

  // Re-fetch when filters change
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      fetchVendors();
    }, 300); // Debounce search
    return () => clearTimeout(timeoutId);
  }, [searchTerm]);

  // Fetch site name from database
  useEffect(() => {
    const fetchSiteName = async () => {
      try {
        const response = await backendApi.getSites(false, resolvedSiteCode);
        if (response.success && Array.isArray(response.sites)) {
          const matchedSite = response.sites.find(
            (item) => item.site_code && item.site_code.toUpperCase() === resolvedSiteCode
          ) || response.sites[0];

          if (matchedSite && matchedSite.site_name) {
            setSiteName(matchedSite.site_name);
          } else {
            setSiteName(resolvedSiteCode);
          }
        }
      } catch (error) {
setSiteName(resolvedSiteCode);
      }
    };

    if (resolvedSiteCode) {
      localStorage.setItem('currentSite', resolvedSiteCode);
      sessionStorage.setItem('tenant', resolvedSiteCode);
      fetchSiteName();
    }
  }, [resolvedSiteCode]);

  const [newVendor, setNewVendor] = useState({
    name: '',
    location: '',
    category: '',
    contactPerson: '',
    email: '',
    phone: '',
    address: '',
    pan: '',
    aadhar: '',
    tan: '',
    gst: '',
    bankName: '',
    acNo: '',
    vendorCode: '',
    poNumber: '',
    poValue: '',
    invoiceDate: '',
    invoiceNumber: '',
    invoiceValue: '',
    gstAmount: '',
    totalInvoiceValue: '',
    invoiceDocument: null,
    remarks: '',
    contractStart: '',
    contractEnd: '',
    paymentTerms: 'Monthly',
    certifications: []
  });

  const categories = ['Solar Panels', 'Inverters', 'Cables & Wires', 'Mounting Systems', 'Tools', 'Safety Equipment', 'Monitoring Systems'];
  const paymentTerms = ['Monthly', 'Quarterly', 'Half-yearly', 'Yearly'];
  const certifications = ['ISO 9001', 'ISO 14001', 'CE', 'UL', 'VDE', 'BIS', 'IEC 61215', 'IEC 61730', 'IEC 60228'];

  // Ensure it's always an array to prevent .map() errors

  /**
   * PAYMENT CALCULATION LOGIC
   * ========================
   * 
   * This system calculates expected payments based on:
   * 1. Contract period (start to end date)
   * 2. Payment schedule (Monthly, Quarterly, Half-yearly, Yearly)
   * 3. Elapsed time from contract start to today
   * 
   * EXAMPLES:
   * 
   * 1) MONTHLY PAYMENT - PO Value: ₹1000, Period: 01Jun-31Mar (10 months)
   *    - Monthly rate: ₹1000 / 10 = ₹100/month
   *    - On 09Jan (7 months elapsed): Expected = 7 × ₹100 = ₹700
   *    - Status: Pending (₹0), Partial (₹1-699), Fully Paid (₹700+)
   * 
   * 2) QUARTERLY PAYMENT - PO Value: ₹1000, Period: 01Jun-31Mar (10 months)
   *    - Monthly rate: ₹1000 / 10 = ₹100/month
   *    - On 09Jan (7 months elapsed): Completed blocks = floor(7/3) = 2 blocks
   *    - Expected = 2 × 3 × ₹100 = ₹600
   *    - Status: Pending (₹0), Partial (₹1-599), Fully Paid (₹600+)
   * 
   * 3) HALF-YEARLY PAYMENT - PO Value: ₹1000, Period: 01Jun-31Mar (10 months)
   *    - Monthly rate: ₹1000 / 10 = ₹100/month
   *    - On 09Jan (7 months elapsed): Completed blocks = floor(7/6) = 1 block
   *    - Expected = 1 × 6 × ₹100 = ₹600
   *    - Status: Pending (₹0), Partial (₹1-599), Fully Paid (₹600+)
   * 
   * 4) YEARLY PAYMENT - PO Value: ₹1000, Period: 01Jun-31Mar (10 months)
   *    - Monthly rate: ₹1000 / 10 = ₹100/month
   *    - On 09Jan (7 months elapsed): Completed blocks = floor(7/12) = 0 blocks
   *    - Expected = 0 × 12 × ₹100 = ₹0
   *    - Status: Pending (₹0), Advance Payment (₹>0)
   */

  // Helper function to calculate months between two dates
  const calculateMonthsBetween = (startDate, endDate) => {
    if (!startDate || !endDate) return 0;
    const start = new Date(startDate);
    const end = new Date(endDate);
    const yearDiff = end.getFullYear() - start.getFullYear();
    const monthDiff = end.getMonth() - start.getMonth();
    return yearDiff * 12 + monthDiff;
  };

  // Helper function to calculate expected payment based on payment schedule
  const calculateExpectedPayment = (vendor) => {
    const poValue = parseFloat(vendor.total_value || 0);
    const contractStart = vendor.contract_start;
    const contractEnd = vendor.contract_end;
    const paymentSchedule = vendor.payment_terms || 'Monthly';

    // If no contract dates, use simple PO value comparison
    if (!contractStart || !contractEnd) {
      return poValue;
    }

    const today = new Date();
    const startDate = new Date(contractStart);
    const endDate = new Date(contractEnd);

    // If contract hasn't started yet, expected payment is 0
    if (today < startDate) {
      return 0;
    }

    // Calculate total contract months
    const totalMonths = calculateMonthsBetween(startDate, endDate);
    if (totalMonths <= 0) return poValue;

    // Calculate elapsed months from contract start to today (capped at contract end)
    const effectiveToday = today > endDate ? endDate : today;
    const elapsedMonths = calculateMonthsBetween(startDate, effectiveToday);

    // Calculate monthly payment rate
    const monthlyRate = poValue / totalMonths;

    // Determine payment block size based on schedule
    let blockSize = 1; // Default: Monthly
    switch (paymentSchedule) {
      case 'Quarterly':
        blockSize = 3;
        break;
      case 'Half-yearly':
        blockSize = 6;
        break;
      case 'Yearly':
        blockSize = 12;
        break;
      case 'Monthly':
      default:
        blockSize = 1;
        break;
    }

    // Calculate number of completed payment blocks
    const completedBlocks = Math.floor(elapsedMonths / blockSize);

    // Calculate expected payment to date
    const expectedPayment = completedBlocks * blockSize * monthlyRate;

    return expectedPayment;
  };

  const filteredVendors = useMemo(() => {
    const data = Array.isArray(vendors) ? vendors : [];
    if (invoiceFilter === 'all') return data;

    return data.filter(vendor => {
      const totalInvoiced = (vendor.invoices || []).reduce((sum, inv) => sum + parseFloat(inv.total_invoice_value || 0), 0);
      const expectedPayment = calculateExpectedPayment(vendor);

      // Fully Paid: Invoiced amount >= Expected payment for elapsed time
      if (invoiceFilter === 'fully_invoiced') {
        // If expected payment is 0 (contract not started or no blocks completed), 
        // consider fully paid only if invoiced matches total PO value
        if (expectedPayment === 0) {
          return totalInvoiced >= parseFloat(vendor.total_value || 0);
        }
        return totalInvoiced >= expectedPayment;
      }

      // Pending: No invoices yet AND expected payment > 0
      if (invoiceFilter === 'pending') {
        return totalInvoiced === 0 && expectedPayment > 0;
      }

      // Partial: Some invoicing but less than expected payment
      if (invoiceFilter === 'partial') {
        return totalInvoiced > 0 && totalInvoiced < expectedPayment;
      }

      return true;
    });
  }, [vendors, invoiceFilter]);

  const parseErrorResponse = (response) => {
    try {
      if (!response) return 'Unknown error occurred';

      // If response.data.errors exists (common structure)
      if (response.data && response.data.errors) {
        const errors = response.data.errors;
        if (errors.non_field_errors && Array.isArray(errors.non_field_errors)) {
          const msg = errors.non_field_errors[0];
          if (msg.toLowerCase().includes('unique set') || msg.toLowerCase().includes('already exists')) {
            return "A vendor with this name already exists for this site.";
          }
          return msg;
        }
        // Handle field specific errors
        const keys = Object.keys(errors);
        if (keys.length > 0) {
          const firstKey = keys[0];
          const firstVal = Array.isArray(errors[firstKey]) ? errors[firstKey][0] : errors[firstKey];
          return `${firstKey}: ${firstVal}`;
        }
        return JSON.stringify(errors);
      }

      // If response.error contains the raw string or object
      if (response.error) {
        let errorObj = response.error;
        if (typeof errorObj === 'string' && (errorObj.startsWith('{') || errorObj.startsWith('['))) {
          try {
            errorObj = JSON.parse(errorObj);
          } catch (e) {
            // Keep as string if parsing fails
          }
        }

        if (errorObj && typeof errorObj === 'object') {
          if (errorObj.non_field_errors && Array.isArray(errorObj.non_field_errors)) {
            const msg = errorObj.non_field_errors[0];
            if (msg.toLowerCase().includes('unique set')) {
              return "A vendor with this name already exists for this site.";
            }
            return msg;
          }
          if (errorObj.detail) return errorObj.detail;
          return JSON.stringify(errorObj);
        }
        return String(errorObj);
      }

      return 'An unexpected error occurred';
    } catch (e) {
      console.error("Error parsing response:", e);
      return 'Failed to process server response';
    }
  };

  const handleAddVendor = async () => {
    setModalError('');
const errors = {};
    if (!newVendor.name) errors.name = true;
    if (!newVendor.category) errors.category = true;
    if (!newVendor.contactPerson) errors.contactPerson = true;

    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      let errorMsg = 'Please fill in required fields: ';
      const missing = [];
      if (errors.name) missing.push('Company Name');
      if (errors.category) missing.push('Category');
      if (errors.contactPerson) missing.push('Contact Person');
      const finalMsg = errorMsg + missing.join(', ');
setModalError(finalMsg);
      showToast('error', 'Validation Error', finalMsg);
      return;
    }

    setFormErrors({});

    const vendorData = {
      name: newVendor.name,
      location: newVendor.location || null,
      category: newVendor.category,
      contact_person: newVendor.contactPerson,
      email: newVendor.email || null,
      phone: newVendor.phone || null,
      address: newVendor.address || null,
      pan: newVendor.pan || null,
      aadhar: newVendor.aadhar || null,
      tan: newVendor.tan || null,
      gst: newVendor.gst || null,
      bank_name: newVendor.bankName || null,
      ac_no: newVendor.acNo || null,
      vendor_code: newVendor.vendorCode || null,
      po_number: newVendor.poNumber || null,
      total_value: newVendor.poValue || 0,
      invoice_date: newVendor.invoiceDate || null,
      invoice_number: newVendor.invoiceNumber || null,
      invoice_value: newVendor.invoiceValue || null,
      gst_amount: newVendor.gstAmount || null,
      total_invoice_value: newVendor.totalInvoiceValue || null,
      remarks: newVendor.remarks || null,
      contract_start: newVendor.contractStart || null,
      contract_end: newVendor.contractEnd || null,
      payment_terms: newVendor.paymentTerms || 'Monthly',
      certifications: newVendor.certifications || []
    };

    // Use FormData if file is present
    let payload = vendorData;
    if (newVendor.invoiceDocument) {
      const formData = new FormData();
      Object.keys(vendorData).forEach(key => {
        if (vendorData[key] !== null && vendorData[key] !== undefined) {
          if (Array.isArray(vendorData[key])) {
            formData.append(key, JSON.stringify(vendorData[key]));
          } else {
            formData.append(key, vendorData[key]);
          }
        }
      });
      formData.append('invoice_document', newVendor.invoiceDocument);
      payload = formData;
    }

    const response = await vendorApi.create(payload, resolvedSiteCode);
    if (response.ok && response.data) {
      await fetchVendors();
      setNewVendor({
        name: '',
        location: '',
        category: '',
        contactPerson: '',
        email: '',
        phone: '',
        address: '',
        pan: '',
        aadhar: '',
        tan: '',
        gst: '',
        bankName: '',
        acNo: '',
        vendorCode: '',
        poNumber: '',
        poValue: '',
        invoiceDate: '',
        invoiceNumber: '',
        invoiceValue: '',
        gstAmount: '',
        totalInvoiceValue: '',
        invoiceDocument: null,
        remarks: '',
        contractStart: '',
        contractEnd: '',
        paymentTerms: 'Monthly',
        certifications: []
      });
      setShowAddModal(false);
      showToast('success', 'Success', 'Vendor added successfully!');
    } else {
      const finalError = parseErrorResponse(response);
      console.error("Vendor create error:", response);
      setModalError(finalError);
      showToast('error', 'Error', finalError);
    }
  };

  const handleDeleteVendor = async (id) => {
    if (window.confirm('Are you sure you want to delete this vendor?')) {
      const response = await vendorApi.delete(id, resolvedSiteCode);
      if (response.ok) {
        await fetchVendors();
        showToast('success', 'Success', 'Vendor deleted successfully!');
      } else {
        showToast('error', 'Error', `Failed to delete vendor: ${response.error}`);
      }
    }
  };

  const handleUpdateVendor = async () => {
    setModalError('');
    if (!editingVendor) {
      showToast('error', 'Error', 'No vendor selected for editing');
      return;
    }

    const errors = {};
    if (!newVendor.name) errors.name = true;
    if (!newVendor.category) errors.category = true;
    if (!newVendor.contactPerson) errors.contactPerson = true;

    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      let errorMsg = 'Please fill in required fields: ';
      const missing = [];
      if (errors.name) missing.push('Company Name');
      if (errors.category) missing.push('Category');
      if (errors.contactPerson) missing.push('Contact Person');
      const finalMsg = errorMsg + missing.join(', ');
setModalError(finalMsg);
      showToast('error', 'Validation Error', finalMsg);
      return;
    }

    setFormErrors({});

    const vendorData = {
      name: newVendor.name,
      location: newVendor.location || null,
      category: newVendor.category,
      contact_person: newVendor.contactPerson,
      email: newVendor.email || null,
      phone: newVendor.phone || null,
      address: newVendor.address || null,
      pan: newVendor.pan || null,
      aadhar: newVendor.aadhar || null,
      tan: newVendor.tan || null,
      gst: newVendor.gst || null,
      bank_name: newVendor.bankName || null,
      ac_no: newVendor.acNo || null,
      vendor_code: newVendor.vendorCode || null,
      po_number: newVendor.poNumber || null,
      total_value: newVendor.poValue || 0,
      invoice_date: newVendor.invoiceDate || null,
      invoice_number: newVendor.invoiceNumber || null,
      invoice_value: newVendor.invoiceValue || null,
      gst_amount: newVendor.gstAmount || null,
      total_invoice_value: newVendor.totalInvoiceValue || null,
      remarks: newVendor.remarks || null,
      contract_start: newVendor.contractStart || null,
      contract_end: newVendor.contractEnd || null,
      payment_terms: newVendor.paymentTerms || 'Monthly',
      certifications: newVendor.certifications || []
    };

    // Use FormData if file is present or creating new one (editing file upload logic might be different)
    // For update, typically we send JSON unless replacing file. 
    // Assuming JSON for edit for now as file upload in edit is separate component usually

    const response = await vendorApi.update(editingVendor.id, vendorData, resolvedSiteCode);
    if (response.ok && response.data) {
      await fetchVendors();
      setEditingVendor(null);
      setShowAddModal(false);
      setNewVendor({
        name: '',
        location: '',
        category: '',
        contactPerson: '',
        email: '',
        phone: '',
        address: '',
        pan: '',
        aadhar: '',
        tan: '',
        gst: '',
        bankName: '',
        acNo: '',
        vendorCode: '',
        poNumber: '',
        poValue: '',
        invoiceDate: '',
        invoiceNumber: '',
        invoiceValue: '',
        gstAmount: '',
        totalInvoiceValue: '',
        invoiceDocument: null,
        remarks: '',
        contractStart: '',
        contractEnd: '',
        paymentTerms: 'Monthly',
        certifications: []
      });
      showToast('success', 'Success', 'Vendor updated successfully!');
    } else {
      const finalError = parseErrorResponse(response);
      console.error("Vendor create error:", response);
      setModalError(finalError);
      showToast('error', 'Error', finalError);
    }
  };

  const handleEditCertificationToggle = (cert) => {
    setEditingVendor({
      ...editingVendor,
      certifications: editingVendor.certifications.includes(cert)
        ? editingVendor.certifications.filter(c => c !== cert)
        : [...editingVendor.certifications, cert]
    });
  };

  const handleCertificationToggle = (cert) => {
    setNewVendor({
      ...newVendor,
      certifications: newVendor.certifications.includes(cert)
        ? newVendor.certifications.filter(c => c !== cert)
        : [...newVendor.certifications, cert]
    });
  };

  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Validate file type
    const validTypes = ['application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', 'application/vnd.ms-excel', 'text/csv'];
    if (!validTypes.includes(file.type) && !file.name.match(/\.(xlsx|xls|csv)$/)) {
      showToast('error', 'Invalid File', 'Please upload a valid Excel or CSV file');
      return;
    }

    try {
      setLoading(true);
      const response = await vendorApi.bulkUpload(file, resolvedSiteCode, 'append');
      if (response.ok) {
        if (response.data?.count === 0 && response.data?.errors?.length > 0) {
          showToast('error', 'Upload Failed', `0 vendors added. ${response.data.errors.length} errors occurred. Check console/logs.`);
} else {
          const created = response.data?.created_count ?? response.data?.count ?? 0;
          const updated = response.data?.updated_count ?? 0;
          const mode = response.data?.upload_mode || 'append';
          showToast('success', 'Success', response.data?.message || `Vendors uploaded (mode: ${mode}, created: ${created}, updated: ${updated})`);
          setShowUploadModal(false);
        }
        await fetchVendors();
      } else {
        showToast('error', 'Upload Failed', response.error || 'Failed to upload vendors');
      }
    } catch (error) {
      console.error('Upload error:', error);
      showToast('error', 'Error', 'An unexpected error occurred during upload');
    } finally {
      setLoading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleInvoiceFileUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Validate file type
    const validTypes = ['application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', 'application/vnd.ms-excel', 'text/csv'];
    if (!validTypes.includes(file.type) && !file.name.match(/\.(xlsx|xls|csv)$/)) {
      showToast('error', 'Invalid File', 'Please upload a valid Excel or CSV file');
      return;
    }

    try {
      setLoading(true);
      const response = await vendorApi.bulkUploadInvoices(file, resolvedSiteCode);
      if (response.ok) {
        showToast('success', 'Success', response.data?.message || 'Invoices uploaded successfully');
        await fetchVendors(); // Refresh vendor list to show updated invoice values
        setShowUploadModal(false);
      } else {
        showToast('error', 'Upload Failed', response.error || 'Failed to upload invoices');
      }
    } catch (error) {
      console.error('Upload error:', error);
      showToast('error', 'Error', 'An unexpected error occurred during upload');
    } finally {
      setLoading(false);
      if (invoiceFileInputRef.current) {
        invoiceFileInputRef.current.value = '';
      }
    }
  };

  const handleExportVendors = () => {
    if (!filteredVendors.length) {
      showToast('error', 'Export Failed', 'No vendors to export');
      return;
    }

    const headers = [
      'Name', 'Location', 'Contact Person', 'Email', 'Phone', 'Address',
      'PAN', 'Aadhar', 'TAN', 'GST', 'Bank Name',
      'Account No', 'Vendor Code', 'PO Number', 'PO Value', 'Payment Schedule',
      'Product and Service', 'Contract Period', 'Invoice Date', 'Last Order', 'User', 'Value (INR)',
      'Invoiced Value', 'PO Balance Amount', 'Remarks'
    ];

    const escapeCsv = (str) => {
      if (!str) return '';
      const stringValue = String(str);
      if (stringValue.includes(',') || stringValue.includes('"') || stringValue.includes('\n')) {
        return `"${stringValue.replace(/"/g, '""')}"`;
      }
      return stringValue;
    };

    const rows = filteredVendors.map(vendor => {
      const totalInvoiced = (vendor.invoices || []).reduce((sum, inv) => sum + parseFloat(inv.total_invoice_value || 0), 0);
      const poValue = parseFloat(vendor.total_value || 0);
      const balance = poValue - totalInvoiced;
      const contractPeriod = vendor.contract_start && vendor.contract_end
        ? `${new Date(vendor.contract_start).toLocaleDateString()} - ${new Date(vendor.contract_end).toLocaleDateString()}`
        : '-';

      return [
        vendor.name,
        vendor.location,
        vendor.contact_person,
        vendor.email,
        vendor.phone,
        vendor.address,
        vendor.pan,
        vendor.aadhar,
        vendor.tan,
        vendor.gst,
        vendor.bank_name,
        vendor.ac_no,
        vendor.vendor_code,
        vendor.po_number,
        poValue,
        vendor.payment_terms,
        vendor.category,
        contractPeriod,
        vendor.invoice_date,
        vendor.last_order,
        vendor.contact_person,
        poValue,
        totalInvoiced,
        balance,
        vendor.remarks
      ].map(escapeCsv).join(',');
    });

    const csvContent = "data:text/csv;charset=utf-8," + headers.join(",") + "\n" + rows.join("\n");
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    link.setAttribute("download", `vendors_export_${new Date().toISOString().slice(0, 10)}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    showToast('success', 'Export Successful', 'Vendors data exported to CSV');
    setShowExportDropdown(false);
  };

  const handleExportInvoices = () => {
    const allInvoices = filteredVendors.flatMap(vendor =>
      (vendor.invoices || []).map(invoice => ({
        ...invoice,
        vendorName: vendor.name,
        vendorLocation: vendor.location,
        vendorCode: vendor.vendor_code,
        poNumber: invoice.po_number || vendor.po_number || ''
      }))
    );

    if (!allInvoices.length) {
      showToast('error', 'Export Failed', 'No invoices to export');
      return;
    }

    const headers = [
      'Invoice Date', 'Invoice No.', 'Invoice Value', 'GST Amount',
      'Total Invoice Value', 'PO Number', 'Vendor Name', 'Vendor Location', 'Vendor Code'
    ];

    const escapeCsv = (str) => {
      if (!str) return '';
      const stringValue = String(str);
      if (stringValue.includes(',') || stringValue.includes('"') || stringValue.includes('\n')) {
        return `"${stringValue.replace(/"/g, '""')}"`;
      }
      return stringValue;
    };

    const rows = allInvoices.map(invoice => {
      return [
        invoice.invoice_date,
        invoice.invoice_number,
        invoice.invoice_value,
        invoice.gst_amount,
        invoice.total_invoice_value,
        invoice.poNumber,
        invoice.vendorName,
        invoice.vendorLocation,
        invoice.vendorCode
      ].map(escapeCsv).join(',');
    });

    const csvContent = "data:text/csv;charset=utf-8," + headers.join(",") + "\n" + rows.join("\n");
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    link.setAttribute("download", `invoices_export_${new Date().toISOString().slice(0, 10)}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    showToast('success', 'Export Successful', 'Invoices data exported to CSV');
    setShowExportDropdown(false);
  };



  const handleAddInvoice = async (e) => {
    e.preventDefault();
    if (!selectedVendorForInvoice) return;

    try {
      setLoading(true);
      const formData = new FormData();
      formData.append('invoice_date', newInvoice.invoiceDate);
      formData.append('invoice_number', newInvoice.invoiceNumber);
      formData.append('invoice_value', newInvoice.invoiceValue);
      formData.append('gst_amount', newInvoice.gstAmount);
      formData.append('total_invoice_value', newInvoice.totalInvoiceValue);
      formData.append('po_number', newInvoice.poNumber);

      if (newInvoice.invoiceDocument) {
        formData.append('invoice_document', newInvoice.invoiceDocument);
      }

      const response = await vendorApi.addInvoice(selectedVendorForInvoice.id, formData, resolvedSiteCode);

      if (response.ok) {
        showToast('success', 'Success', 'Invoice added successfully');
        setShowInvoiceModal(false);
        setNewInvoice({
          invoiceDate: '',
          invoiceNumber: '',
          invoiceValue: '',
          gstAmount: '',
          totalInvoiceValue: '',
          poNumber: '',
          invoiceDocument: null
        });
        await fetchVendors();
      } else {
        showToast('error', 'Error', response.error || 'Failed to add invoice');
      }
    } catch (error) {
      console.error('Add invoice error:', error);
      showToast('error', 'Error', 'An unexpected error occurred');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-[#0B1120] flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-[#0084CF]/30 border-t-indigo-500 rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-900 dark:text-slate-100 font-semibold text-lg">Loading vendors...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen vendor-page np-page">
      <ToastNotification
        show={toast.show}
        type={toast.type}
        title={toast.title}
        message={toast.message}
        onClose={() => setToast({ ...toast, show: false })}
      />

      <style
        dangerouslySetInnerHTML={{
          __html: `
            /* Structure only — typography and colour come from styles/np-theme.css */
            .vendor-page .inventory-table { border-collapse: separate !important; border-spacing: 0 !important; width: 100%; }
            .vendor-page .inventory-table-thead { display: table-header-group !important; position: sticky !important; top: 0 !important; z-index: 50 !important; }
            .vendor-page .inventory-table-thead tr { display: table-row; }
            .vendor-page .inventory-table thead th,
            .vendor-page .inventory-table-thead th,
            .vendor-page .inventory-table-header {
              display: table-cell !important;
              padding: 11px 16px !important;
              text-align: center !important;
              white-space: nowrap !important;
              position: relative !important;
              min-width: 100px !important;
            }
            .vendor-page .inventory-table thead th:last-child { border-right: none !important; }
            .vendor-page .inventory-table tbody td { font-size: var(--np-fs-sm) !important; }

            /* Popup form labels / headers: the reference micro-label */
            .vendor-page .vendor-popup-label,
            .vendor-page .vendor-popup-header {
              color: var(--np-subtle) !important;
              text-transform: uppercase !important;
              letter-spacing: 0.08em !important;
              font-size: var(--np-fs-micro) !important;
            }
            .vendor-page .vendor-popup-label { text-align: left !important; display: block !important; }
            .vendor-page .vendor-popup-input { color: var(--np-ink) !important; background-color: #ffffff !important; text-align: left !important; }
            .vendor-page .force-normal-black { color: var(--np-ink) !important; }

            /* Sticky Actions + Name columns */
            .vendor-page .inventory-table thead th:first-child,
            .vendor-page .inventory-table tbody td:first-child { position: sticky !important; left: 0 !important; z-index: 10 !important; }
            .vendor-page .inventory-table thead th:nth-child(2),
            .vendor-page .inventory-table tbody td:nth-child(2) { position: sticky !important; left: 96px !important; z-index: 9 !important; }
            .vendor-page .inventory-table thead th:first-child { z-index: 101 !important; }
            .vendor-page .inventory-table thead th:nth-child(2) { z-index: 100 !important; }
            .vendor-page .inventory-table tbody td:first-child,
            .vendor-page .inventory-table tbody td:nth-child(2) { background: var(--np-surface) !important; color: var(--np-ink) !important; }
            .vendor-page .inventory-table tbody td:nth-child(2) { box-shadow: 1px 0 0 var(--np-line) !important; }
            .vendor-page .inventory-table tbody tr:hover td:first-child,
            .vendor-page .inventory-table tbody tr:hover td:nth-child(2),
            .vendor-page .inventory-table tbody tr.bg-[#F2F8FC]\/50 td:first-child,
            .vendor-page .inventory-table tbody tr.bg-[#F2F8FC]\/60 td:first-child,
            .vendor-page .inventory-table tbody tr.bg-[#F2F8FC]\/50 td:nth-child(2),
            .vendor-page .inventory-table tbody tr.bg-[#F2F8FC]\/60 td:nth-child(2) { background: var(--np-tint) !important; }

            /* Invoice popup table uses semantic surface */
            .vendor-page .invoice-popup-table tr,
            .vendor-page .invoice-popup-table td,
            .vendor-page .invoice-popup-tbody tr,
            .vendor-page .invoice-popup-tbody td,
            .vendor-page .inventory-table tbody tr:hover .invoice-popup-table td { background: var(--np-surface) !important; color: var(--np-ink) !important; }
          `,
        }}
      />

      {/* Main Content — np-theme scope starts here so the shared PrivateHeader above keeps its own styling */}
      <div className="np-theme w-auto ml-4 mr-10 px-3 sm:px-4 py-2 pt-20 sm:pt-24 mt-16 overflow-x-hidden">
        {/* Header Section */}
        <div className="mb-4">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 mb-4">
            <div className="np-pagehead">
              <div className="np-pagehead__mark">
                <Users className="w-5 h-5" />
              </div>
              <div className="min-w-0">
                <h1 className="np-pagehead__title">Vendor Management</h1>
                <div className="np-pagehead__meta">
                  <span className="np-pagehead__site">{siteName || resolvedSiteCode || 'SITE'}</span>
                  <span className="np-pagehead__desc">Supplier Relationships & Contracts</span>
                </div>
              </div>
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
              <input
                type="file"
                ref={fileInputRef}
                className="hidden"
                accept=".xlsx,.xls,.csv"
                onChange={handleFileUpload}
              />
              <input
                type="file"
                ref={invoiceFileInputRef}
                className="hidden"
                accept=".xlsx,.xls,.csv"
                onChange={handleInvoiceFileUpload}
              />
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => {
                  setShowUploadModal(true);
                  setShowExportDropdown(false);
                }}
                className="flex items-center gap-1.5 px-3 py-1.5 bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-all shadow-sm text-sm"
                title="Upload Vendors from Excel/CSV" aria-label="Upload Vendors from Excel/CSV"
              >
                <Upload className="w-4 h-4 text-emerald-600" />
                <span className="hidden sm:inline">Bulk Upload</span>
              </motion.button>
              <div className="relative">
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => {
                    setShowExportDropdown(!showExportDropdown);
                    setShowUploadModal(false);
                  }}
                  className="flex items-center gap-1.5 px-3 py-1.5 bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-all shadow-sm text-sm"
                  title="Export Data" aria-label="Export Data"
                >
                  <Download className="w-4 h-4 text-[#0066A3]" />
                  <span className="hidden sm:inline">Export</span>
                  <ChevronDown className="w-3 h-3 text-gray-400" />
                </motion.button>

                <AnimatePresence>
                  {showExportDropdown && (
                    <motion.div
                      initial={{ opacity: 0, scale: 0.95, y: -10 }}
                      animate={{ opacity: 1, scale: 1, y: 0 }}
                      exit={{ opacity: 0, scale: 0.95, y: -10 }}
                      className="absolute right-0 mt-2 w-48 bg-white rounded-xl shadow-xl border border-gray-100 overflow-hidden z-[120]"
                    >
                      <button
                        onClick={handleExportVendors}
                        className="w-full text-left px-4 py-3 text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2 border-b border-gray-50"
                      >
                        <Users className="w-4 h-4 text-[#0084CF]" />
                        <div>
                          <span className="force-normal-black block">Vendor Data</span>
                          <span className="text-xs force-normal-black block">Export master list</span>
                        </div>
                      </button>
                      <button
                        onClick={handleExportInvoices}
                        className="w-full text-left px-4 py-3 text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2"
                      >
                        <FileText className="w-4 h-4 text-emerald-500" />
                        <div>
                          <span className="force-normal-black block">Invoice Data</span>
                          <span className="text-xs force-normal-black block">Export all invoices</span>
                        </div>
                      </button>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => fetchVendors()}
                className="flex items-center gap-1.5 px-3 py-1.5 bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-all shadow-sm text-sm"
                title="Refresh vendors" aria-label="Refresh vendors"
              >
                <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
                <span className="hidden sm:inline">Refresh</span>
              </motion.button>
            </div>
          </div>

          {/* Stats Cards */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-2 mb-6">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="np-kpi"
            >
              <div className="np-kpi__head">
                <p className="np-kpi__label">Total Vendors</p>
                <span className="np-kpi__mark">
                  <Users className="w-3.5 h-3.5" />
                </span>
              </div>
              <p className="np-kpi__value">
                {vendorStats.total}
              </p>
              <p className="np-kpi__hint">Active suppliers on record</p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="np-kpi"
            >
              <div className="np-kpi__head">
                <p className="np-kpi__label">Active Contracts</p>
                <span className="np-kpi__mark">
                  <CheckCircle className="w-3.5 h-3.5" />
                </span>
              </div>
              <p className="np-kpi__value">
                {vendorStats.active}
              </p>
              <p className="np-kpi__hint">Vendors with active status</p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="np-kpi"
            >
              <div className="np-kpi__head">
                <p className="np-kpi__label">Pending Renewal</p>
                <span className="np-kpi__mark">
                  <AlertCircle className="w-3.5 h-3.5" />
                </span>
              </div>
              <p className="np-kpi__value">
                {vendorStats.pendingRenewal}
              </p>
              <p className="np-kpi__hint">Expiring in next 60 days</p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="np-kpi"
            >
              <div className="np-kpi__head">
                <p className="np-kpi__label">Business Value (PO)</p>
                <span className="np-kpi__mark">
                  <IndianRupee className="w-3.5 h-3.5" />
                </span>
              </div>
              <p className="np-kpi__value">
                ₹{(vendorStats.totalValue || 0).toLocaleString('en-IN')}
              </p>
              <p className="np-kpi__hint">Total purchase order value</p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
              className="np-kpi"
            >
              <div className="np-kpi__head">
                <p className="np-kpi__label">Invoiced Value</p>
                <span className="np-kpi__mark">
                  <FileText className="w-3.5 h-3.5" />
                </span>
              </div>
              <p className="np-kpi__value">
                ₹{(vendorStats.totalInvoicedValue || 0).toLocaleString('en-IN')}
              </p>
              <p className="np-kpi__hint">Total value invoiced to date</p>
            </motion.div>
          </div>
        </div>

        {/* Controls */}
        <div className="bg-white rounded-xl px-2 py-2 mb-2 flex flex-wrap items-center justify-between gap-3">
          <div className="flex flex-wrap items-center gap-4 flex-1">
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <input
                type="text"
                placeholder="Search vendors..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-[#E3F0F9] rounded-lg focus:ring-2 focus:ring-[#0084CF] bg-[#F2F8FC]/30 text-sm"
              />
            </div>

            <div className="flex items-center gap-2">
              <span className="text-xs font-black text-slate-900 uppercase tracking-wider" style={{ fontWeight: 900 }}>Invoicing:</span>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setInvoiceFilter('all')}
                  className={`px-3 py-1.5 rounded-lg text-sm font-black transition-all ${invoiceFilter === 'all'
                    ? 'bg-[#0066A3] text-white shadow-md'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  style={{ fontWeight: 900 }}
                >
                  All
                </button>
                <button
                  onClick={() => setInvoiceFilter('fully_invoiced')}
                  className={`px-3 py-1.5 rounded-lg text-sm font-black transition-all ${invoiceFilter === 'fully_invoiced'
                    ? 'bg-green-600 text-white shadow-md'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  style={{ fontWeight: 900 }}
                >
                  Fully Invoiced
                </button>
                <button
                  onClick={() => setInvoiceFilter('partial')}
                  className={`px-3 py-1.5 rounded-lg text-sm font-black transition-all ${invoiceFilter === 'partial'
                    ? 'bg-yellow-600 text-white shadow-md'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  style={{ fontWeight: 900 }}
                >
                  Partial
                </button>
                <button
                  onClick={() => setInvoiceFilter('pending')}
                  className={`px-3 py-1.5 rounded-lg text-sm font-black transition-all ${invoiceFilter === 'pending'
                    ? 'bg-orange-600 text-white shadow-md'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  style={{ fontWeight: 900 }}
                >
                  Pending
                </button>
              </div>
            </div>
          </div>

          <motion.button
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            onClick={() => setShowAddModal(true)}
            className="flex items-center gap-2 px-4 py-2 bg-[#0066A3] text-white rounded-lg hover:bg-[#1C4D88] transition-all shadow-md shadow-[#0084CF]/20 font-black text-sm"
            style={{ fontWeight: 900 }}
          >
            <Plus className="w-4 h-4 stroke-[3px]" />
            Add New Vendor
          </motion.button>
        </div>

        {/* Vendors Table Sheet */}
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden mb-8">
          <div className="overflow-x-auto">
            <table className="inventory-table np-table border-collapse" style={{ tableLayout: 'fixed', width: '100%', minWidth: `${columnWidths.reduce((sum, width) => sum + width, 0)}px`, borderCollapse: 'separate', borderSpacing: 0 }}>
              <thead className="inventory-table-thead" style={{ position: 'sticky', top: 0, zIndex: 100 }}>
                <tr>
                  {[
                    'Actions',
                    'Name',
                    'Location',
                    'PAN',
                    'Aadhar',
                    'TAN',
                    'GST',
                    'Bank Name',
                    'Account No',
                    'Vendor Code',
                    'PO Number',
                    'PO Value',
                    'Payment Schedule',
                    'Product and Service',
                    'Contract Period',
                    'User',
                    'Value (₹)',
                    'Invoiced Value',
                    'PO Balance Amount',
                    'Remarks'
                  ].map((header, index) => (
                    <th key={index} className={`inventory-table-header relative ${index === 0 ? 'border-r-0' : ''}`} style={getHeaderStyle(index)}>
                      {header}
                      {index !== 0 && (
                        <div
                          className="absolute top-0 right-0 w-1 h-full cursor-col-resize hover:bg-[#3EA3DA] bg-transparent z-10"
                          onMouseDown={(e) => handleResizeStart(index, e)}
                          title="Drag to resize column"
                        />
                      )}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredVendors.length > 0 ? (
                  filteredVendors.map((vendor, index) => (
                    <React.Fragment key={vendor.id}>
                      <motion.tr
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: Math.min(index, 8) * 0.02 }}
                        className={`${expandedRow === vendor.id ? 'bg-[#F2F8FC]/50 hover:bg-[#F2F8FC]/60' : 'hover:bg-[#F2F8FC]/30'} transition-colors group border-b border-gray-100 cursor-pointer`}
                        onClick={() => {
                          setExpandedRow(prev => prev === vendor.id ? null : vendor.id);
                        }}
                      >
                        {/* Actions Column - Now First */}
                        <td className="px-2 py-1.5 border-r-0 border-gray-100" style={{ width: `${columnWidths[0]}px`, minWidth: `${columnWidths[0]}px`, maxWidth: `${columnWidths[0]}px`, overflow: 'hidden', textAlign: 'center' }}>
                          <div className="flex items-center justify-center gap-1" onClick={(e) => e.stopPropagation()}>
                            <motion.button
                              whileHover={{ scale: 1.1 }}
                              whileTap={{ scale: 0.9 }}
                              onClick={() => {
                                setEditingVendor(vendor);
                                setNewVendor({
                                  name: vendor.name || '',
                                  location: vendor.location || '',
                                  category: vendor.category || '',
                                  contactPerson: vendor.contact_person || '',
                                  email: vendor.email || '',
                                  phone: vendor.phone || '',
                                  address: vendor.address || '',
                                  pan: vendor.pan || '',
                                  aadhar: vendor.aadhar || '',
                                  tan: vendor.tan || '',
                                  gst: vendor.gst || '',
                                  bankName: vendor.bank_name || '',
                                  acNo: vendor.ac_no || '',
                                  vendorCode: vendor.vendor_code || '',
                                  poNumber: vendor.po_number || '',
                                  invoiceDate: vendor.invoice_date || '',
                                  remarks: vendor.remarks || '',
                                  contractStart: vendor.contract_start || '',
                                  contractEnd: vendor.contract_end || '',
                                  paymentTerms: vendor.payment_terms || 'Monthly',
                                  certifications: vendor.certifications || []
                                });
                                setShowAddModal(true);
                              }}
                              className="p-1 text-[#0066A3] hover:bg-[#E3F0F9] rounded-lg transition-colors"
                              title="Edit vendor" aria-label="Edit vendor"
                            >
                              <Edit className="w-3.5 h-3.5" />
                            </motion.button>
                            <motion.button
                              whileHover={{ scale: 1.1 }}
                              whileTap={{ scale: 0.9 }}
                              onClick={() => handleDeleteVendor(vendor.id)}
                              className="p-1 text-red-600 hover:bg-red-100 rounded-lg transition-colors"
                              title="Delete vendor" aria-label="Delete vendor"
                            >
                              <Trash2 className="w-3.5 h-3.5" />
                            </motion.button>
                          </div>
                        </td>
                        {/* Name */}
                        <td className="px-2 py-1.5 border-r border-gray-100" style={{ width: `${columnWidths[1]}px`, minWidth: `${columnWidths[1]}px`, maxWidth: `${columnWidths[1]}px`, overflow: 'hidden' }}>
                          <span className="text-sm text-gray-900 font-sans truncate block inventory-table-text text-center" title={vendor.name}>{vendor.name || '-'}</span>
                        </td>
                        {/* Location */}
                        <td className="px-2 py-1.5 border-r border-gray-100" style={{ width: `${columnWidths[2]}px`, minWidth: `${columnWidths[2]}px`, maxWidth: `${columnWidths[2]}px`, overflow: 'hidden' }}>
                          <span className="text-sm text-gray-900 font-sans truncate block inventory-table-text text-center" title={vendor.location}>{vendor.location || '-'}</span>
                        </td>
                        {/* PAN */}
                        <td className="px-2 py-1.5 border-r border-gray-100" style={{ width: `${columnWidths[3]}px`, minWidth: `${columnWidths[3]}px`, maxWidth: `${columnWidths[3]}px`, overflow: 'hidden' }}>
                          <span className="text-sm text-gray-900 font-mono truncate block text-center" title={vendor.pan}>{vendor.pan || '-'}</span>
                        </td>
                        {/* Aadhar */}
                        <td className="px-2 py-1.5 border-r border-gray-100" style={{ width: `${columnWidths[4]}px`, minWidth: `${columnWidths[4]}px`, maxWidth: `${columnWidths[4]}px`, overflow: 'hidden' }}>
                          <span className="text-sm text-gray-900 font-mono truncate block text-center" title={maskAadhar(vendor.aadhar)}>{maskAadhar(vendor.aadhar) || '-'}</span>
                        </td>
                        {/* TAN */}
                        <td className="px-2 py-1.5 border-r border-gray-100" style={{ width: `${columnWidths[5]}px`, minWidth: `${columnWidths[5]}px`, maxWidth: `${columnWidths[5]}px`, overflow: 'hidden' }}>
                          <span className="text-sm text-gray-900 font-mono truncate block text-center" title={vendor.tan}>{vendor.tan || '-'}</span>
                        </td>
                        {/* GST */}
                        <td className="px-2 py-1.5 border-r border-gray-100" style={{ width: `${columnWidths[6]}px`, minWidth: `${columnWidths[6]}px`, maxWidth: `${columnWidths[6]}px`, overflow: 'hidden' }}>
                          <span className="text-sm text-gray-900 font-mono truncate block text-center" title={vendor.gst}>{vendor.gst || '-'}</span>
                        </td>
                        {/* Bank Name */}
                        <td className="px-2 py-1.5 border-r border-gray-100" style={{ width: `${columnWidths[7]}px`, minWidth: `${columnWidths[7]}px`, maxWidth: `${columnWidths[7]}px`, overflow: 'hidden' }}>
                          <span className="text-sm text-gray-900 font-sans truncate block inventory-table-text text-center" title={vendor.bank_name}>{vendor.bank_name || '-'}</span>
                        </td>
                        {/* Account No */}
                        <td className="px-2 py-1.5 border-r border-gray-100" style={{ width: `${columnWidths[8]}px`, minWidth: `${columnWidths[8]}px`, maxWidth: `${columnWidths[8]}px`, overflow: 'hidden' }}>
                          <span className="text-sm text-gray-900 font-mono truncate block text-center" title={vendor.ac_no}>{vendor.ac_no || '-'}</span>
                        </td>
                        {/* Vendor Code */}
                        <td className="px-2 py-1.5 border-r border-gray-100" style={{ width: `${columnWidths[9]}px`, minWidth: `${columnWidths[9]}px`, maxWidth: `${columnWidths[9]}px`, overflow: 'hidden' }}>
                          <span className="text-sm text-gray-900 font-mono truncate block text-center" title={vendor.vendor_code}>{vendor.vendor_code || '-'}</span>
                        </td>
                        {/* PO Number */}
                        <td className="px-2 py-1.5 border-r border-gray-100" style={{ width: `${columnWidths[10]}px`, minWidth: `${columnWidths[10]}px`, maxWidth: `${columnWidths[10]}px`, overflow: 'hidden' }}>
                          <span className="text-sm text-gray-900 font-mono truncate block text-center" title={vendor.po_number}>{vendor.po_number || '-'}</span>
                        </td>
                        {/* PO Value */}
                        <td className="px-2 py-1.5 border-r border-gray-100" style={{ width: `${columnWidths[11]}px`, minWidth: `${columnWidths[11]}px`, maxWidth: `${columnWidths[11]}px`, overflow: 'hidden' }}>
                          <span className="text-sm text-gray-900 font-bold font-sans truncate block text-center" style={{ fontWeight: 800 }}>
                            {parseFloat(vendor.total_value || 0).toLocaleString('en-IN', {
                              style: 'currency',
                              currency: 'INR',
                              maximumFractionDigits: 0
                            })}
                          </span>
                        </td>
                        {/* Payment Schedule */}
                        <td className="px-2 py-1.5 border-r border-gray-100" style={{ width: `${columnWidths[12]}px`, minWidth: `${columnWidths[12]}px`, maxWidth: `${columnWidths[12]}px`, overflow: 'hidden' }}>
                          <span className="text-sm text-gray-900 font-sans truncate block text-center">{vendor.payment_terms || '-'}</span>
                        </td>
                        {/* Product and Service */}
                        <td className="px-2 py-1.5 border-r border-gray-100" style={{ width: `${columnWidths[13]}px`, minWidth: `${columnWidths[13]}px`, maxWidth: `${columnWidths[13]}px`, overflow: 'hidden' }}>
                          <span className="text-sm text-gray-900 font-sans truncate block text-center">{vendor.category || '-'}</span>
                        </td>
                        {/* Contract Period */}
                        <td className="px-2 py-1.5 border-r border-gray-100" style={{ width: `${columnWidths[14]}px`, minWidth: `${columnWidths[14]}px`, maxWidth: `${columnWidths[14]}px`, overflow: 'hidden' }}>
                          <span className="text-sm text-gray-900 font-sans truncate block text-center">
                            {vendor.contract_start && vendor.contract_end
                              ? `${formatDate(vendor.contract_start)} - ${formatDate(vendor.contract_end)}`
                              : '-'}
                          </span>
                        </td>
                        {/* User */}
                        <td className="px-2 py-1.5 border-r border-gray-100" style={{ width: `${columnWidths[15]}px`, minWidth: `${columnWidths[15]}px`, maxWidth: `${columnWidths[15]}px`, overflow: 'hidden' }}>
                          <span className="text-sm text-gray-900 font-sans truncate block text-center" title={vendor.contact_person}>{vendor.contact_person || '-'}</span>
                        </td>
                        {/* Value (₹) */}
                        <td className="px-2 py-1.5 border-r border-gray-100" style={{ width: `${columnWidths[16]}px`, minWidth: `${columnWidths[16]}px`, maxWidth: `${columnWidths[16]}px`, overflow: 'hidden' }}>
                          <span className="text-sm text-gray-900 font-bold font-sans truncate block text-center" style={{ fontWeight: 800 }}>
                            {parseFloat(vendor.total_value || 0).toLocaleString('en-IN', {
                              style: 'currency',
                              currency: 'INR',
                              maximumFractionDigits: 0
                            })}
                          </span>
                        </td>
                        {/* Invoiced Value */}
                        <td className="px-2 py-1.5 border-r border-gray-100 hover:bg-[#F2F8FC]/50"
                          style={{ width: `${columnWidths[17]}px`, minWidth: `${columnWidths[17]}px`, maxWidth: `${columnWidths[17]}px`, overflow: 'hidden' }}
                        >
                          <div className="flex items-center justify-center gap-1 w-full">
                            <span className="text-sm text-gray-900 font-bold font-sans truncate block text-center flex-1" style={{ fontWeight: 800 }}>
                              {(() => {
                                const totalInvoiced = (vendor.invoices || []).reduce((sum, inv) => sum + parseFloat(inv.total_invoice_value || 0), 0);
                                return totalInvoiced.toLocaleString('en-IN', {
                                  style: 'currency',
                                  currency: 'INR',
                                  maximumFractionDigits: 0
                                });
                              })()}
                            </span>
                            <ChevronDown className={`w-3 h-3 text-gray-400 transition-transform duration-200 flex-shrink-0 ${expandedRow === vendor.id ? 'rotate-180' : ''}`} />
                          </div>
                        </td>
                        {/* PO Balance Amount */}
                        <td className="px-2 py-1.5 border-r border-gray-100" style={{ width: `${columnWidths[18]}px`, minWidth: `${columnWidths[18]}px`, maxWidth: `${columnWidths[18]}px`, overflow: 'hidden' }}>
                          <span className="text-sm text-gray-900 font-bold font-sans truncate block text-center" style={{ fontWeight: 800 }} title={`Expected: ${calculateExpectedPayment(vendor).toLocaleString('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 })}`}>
                            {(() => {
                              const totalInvoiced = (vendor.invoices || []).reduce((sum, inv) => sum + parseFloat(inv.total_invoice_value || 0), 0);
                              const expectedPayment = calculateExpectedPayment(vendor);
                              const balance = expectedPayment - totalInvoiced;
                              return balance.toLocaleString('en-IN', {
                                style: 'currency',
                                currency: 'INR',
                                maximumFractionDigits: 0
                              });
                            })()}
                          </span>
                        </td>
                        {/* Remarks */}
                        <td className="px-2 py-1.5 border-r border-gray-100" style={{ width: `${columnWidths[19]}px`, minWidth: `${columnWidths[19]}px`, maxWidth: `${columnWidths[19]}px`, overflow: 'hidden' }}>
                          <span className="text-sm text-gray-900 font-sans truncate block inventory-table-text text-center" title={vendor.remarks}>{vendor.remarks || '-'}</span>
                        </td>
                      </motion.tr>
                      {expandedRow === vendor.id && (
                        <tr className="bg-transparent">
                          <td colSpan={20} className="p-0 border-b border-gray-200 relative">
                            <div className="vendor-history-wrapper">
                              <motion.div
                                initial={{ height: 0, opacity: 0, y: -10 }}
                                animate={{ height: 'auto', opacity: 1, y: 0 }}
                                exit={{ height: 0, opacity: 0, y: -10 }}
                                transition={{ duration: 0.2 }}
                                className="relative z-[5] shadow-xl rounded-b-lg overflow-hidden border-x border-b border-gray-200 w-full"
                              >
                                <div className="bg-white p-1.5">
                                  <h4 className="flex items-left justify-left gap-2 text-[16px] font-[800] text-gray-900 mb-1 pb-1.5 border-b border-gray-100 uppercase tracking-wide text-center">
                                    Invoice Details
                                  </h4>

                                  <div className="overflow-x-auto">
                                    <table className="w-full text-left border-collapse invoice-popup-table table-fixed">
                                      <thead className="bg-[#0084CF]">
                                        <tr>
                                          <th className="px-1.5 py-1 w-[14.28%] text-[9px] font-[800] text-white uppercase tracking-wider border-b border-[#3EA3DA] text-center">Invoice Date</th>
                                          <th className="px-1.5 py-1 w-[14.28%] text-[9px] font-[800] text-white uppercase tracking-wider border-b border-[#3EA3DA] text-center">Invoice No.</th>
                                          <th className="px-1.5 py-1 w-[14.28%] text-[9px] font-[800] text-white uppercase tracking-wider border-b border-[#3EA3DA] text-center">Invoice Value</th>
                                          <th className="px-1.5 py-1 w-[14.28%] text-[9px] font-[800] text-white uppercase tracking-wider border-b border-[#3EA3DA] text-center">GST Amount</th>
                                          <th className="px-1.5 py-1 w-[14.28%] text-[9px] font-[800] text-white uppercase tracking-wider border-b border-[#3EA3DA] text-center">Total Invoice Value</th>
                                          <th className="px-1.5 py-1 w-[14.28%] text-[9px] font-[800] text-white uppercase tracking-wider border-b border-[#3EA3DA] text-center">PO Number</th>
                                          <th className="px-1.5 py-1 w-[14.28%] text-[9px] font-[800] text-white uppercase tracking-wider border-b border-[#3EA3DA] text-center">Invoice Document</th>
                                        </tr>
                                      </thead>

                                      <tbody className="divide-y divide-gray-50 invoice-popup-tbody">
                                        {vendor.invoices && vendor.invoices.length > 0 ? (
                                          vendor.invoices.map((invoice, i) => (
                                            <tr key={invoice.id || i} style={{ backgroundColor: 'white' }}>
                                              <td style={{ backgroundColor: 'white' }} className="px-1.5 py-1 text-[9px] font-[800] text-gray-900 text-center">{formatDate(invoice.invoice_date)}</td>
                                              <td style={{ backgroundColor: 'white' }} className="px-1.5 py-1 text-[9px] font-[800] text-gray-900 text-center">{invoice.invoice_number}</td>
                                              <td style={{ backgroundColor: 'white' }} className="px-1.5 py-1 text-[9px] font-[800] text-gray-900 text-center">{parseFloat(invoice.invoice_value || 0).toLocaleString()}</td>
                                              <td style={{ backgroundColor: 'white' }} className="px-1.5 py-1 text-[9px] font-[800] text-gray-900 text-center">{parseFloat(invoice.gst_amount || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}</td>
                                              <td style={{ backgroundColor: 'white' }} className="px-1.5 py-1 text-[9px] font-[800] text-gray-900 text-center">{parseFloat(invoice.total_invoice_value || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}</td>
                                              <td style={{ backgroundColor: 'white' }} className="px-1.5 py-1 text-[9px] font-[800] text-gray-900 text-center">{invoice.po_number || vendor.po_number || '-'}</td>
                                              <td style={{ backgroundColor: 'white' }} className="px-1.5 py-1 text-center">
                                                {invoice.invoice_document ? (
                                                  <a href={invoice.invoice_document} target="_blank" rel="noopener noreferrer" className="text-red-500 hover:text-red-600 hover:bg-red-50 p-1 rounded transition-colors inline-block" title="Download PDF">
                                                    <FileText className="w-3.5 h-3.5" />
                                                  </a>
                                                ) : (
                                                  <span className="text-gray-300">-</span>
                                                )}
                                              </td>
                                            </tr>
                                          ))
                                        ) : (
                                          <tr>
                                            <td colSpan="7" className="px-1.5 py-3 text-center text-[10px] text-gray-500">
                                              No invoices found
                                            </td>
                                          </tr>
                                        )}
                                      </tbody>
                                    </table>
                                    <div className="mt-2 px-4 py-1.5 bg-gray-50 border-t border-gray-200 rounded-b flex items-center justify-end gap-4">
                                      <button
                                        className="text-[12px] font-black text-[#1C4D88] hover:text-[#0D2747] flex items-center justify-center gap-1 disabled:opacity-50 disabled:cursor-not-allowed"
                                        disabled={!vendor.invoices || vendor.invoices.length === 0}
                                        onClick={() => {
                                          if (!vendor.invoices || vendor.invoices.length === 0) return;

                                          const csvHeaders = ["Invoice Date", "Invoice No.", "Invoice Value", "GST Amount", "Total Invoice Value", "PO Number", "Name", "Location"];
                                          const rows = vendor.invoices.map(invoice => {
                                            return [
                                              invoice.invoice_date,
                                              invoice.invoice_number,
                                              invoice.invoice_value,
                                              invoice.gst_amount,
                                              invoice.total_invoice_value,
                                              invoice.po_number || vendor.po_number || '',
                                              `"${vendor.name || ''}"`,
                                              `"${vendor.location || ''}"`
                                            ].join(",");
                                          });

                                          const csvContent = "data:text/csv;charset=utf-8,"
                                            + csvHeaders.join(",") + "\n"
                                            + rows.join("\n");

                                          const encodedUri = encodeURI(csvContent);
                                          const link = document.createElement("a");
                                          link.setAttribute("href", encodedUri);
                                          link.setAttribute("download", `invoices_${vendor.name || 'vendor'}.csv`);
                                          document.body.appendChild(link);
                                          link.click();
                                          document.body.removeChild(link);
                                        }}
                                        style={{ fontWeight: 900 }}
                                      >
                                        View All Invoices <ArrowRight className="w-3 h-3 stroke-[3px]" />
                                      </button>

                                      <div className="flex items-center gap-2">
                                        <button
                                          onClick={() => {
                                            setSelectedVendorForInvoice(vendor);
                                            setNewInvoice(prev => ({ ...prev, poNumber: vendor.po_number || '' }));
                                            setShowInvoiceModal(true);
                                          }}
                                          className="flex items-center gap-1.5 px-4 py-1.5 bg-white border border-[#8AC4E6] rounded hover:bg-[#F2F8FC] transition-colors shadow-sm font-black"
                                          style={{ fontWeight: 900 }}
                                        >
                                          <Plus className="w-2.5 h-2.5 text-[#0066A3] stroke-[3px]" />
                                          <span className="text-[9px] font-black text-[#1C4D88]" style={{ fontWeight: 900 }}>Add Invoice</span>
                                        </button>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              </motion.div>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  ))
                ) : (
                  <tr>
                    <td colSpan="20" className="text-center py-12">
                      <div className="flex flex-col items-center justify-center">
                        <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-4">
                          <Users className="w-8 h-8 text-gray-400" />
                        </div>
                        <h3 className="text-lg font-medium text-gray-900 mb-1">No vendors found</h3>
                        <p className="text-gray-500 text-sm max-w-sm mx-auto">
                          {searchTerm || invoiceFilter !== 'all'
                            ? 'Try adjusting your search or filters to find what you are looking for.'
                            : 'Get started by adding your first vendor to the system.'}
                        </p>
                        <motion.button
                          whileHover={{ scale: 1.05 }}
                          whileTap={{ scale: 0.95 }}
                          onClick={() => setShowAddModal(true)}
                          className="mt-6 flex items-center gap-2 px-4 py-2 bg-[#0066A3] text-white rounded-lg hover:bg-[#1C4D88] transition-colors shadow-sm"
                        >
                          <Plus className="w-4 h-4" />
                          Add Vendor
                        </motion.button>
                      </div>
                    </td>
                  </tr>
                )
                }
              </tbody>
            </table>
          </div>
        </div>

        {/* Add/Edit Modal */}
        <AnimatePresence>
          {showAddModal && (
            <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center z-[10000] p-4 pt-20">
              <motion.div
                initial={{ opacity: 0, scale: 0.95, y: 20 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.95, y: 20 }}
                className="bg-white rounded-2xl border border-slate-100 shadow-2xl w-full max-w-4xl max-h-[85vh] flex flex-col overflow-hidden"
              >
                {/* Modal Header */}
                <div className="bg-gradient-to-r from-[#0066A3] to-[#0066A3] px-6 py-4 flex items-center justify-between shrink-0">
                  <div className="flex-1 text-center">
                    <h3 className="text-xl font-[800] text-white">
                      {editingVendor ? 'Edit Vendor' : 'Add New Vendor'}
                    </h3>
                    <p className="text-[#E3F0F9] text-sm mt-0.5">
                      {editingVendor ? 'Update vendor information' : 'Register a new supplier'}
                    </p>
                  </div>
                  <button
                    onClick={() => {
                      setFormErrors({});
                      setModalError('');
                      setShowAddModal(false);
                      setEditingVendor(null);
                      setNewVendor({
                        name: '',
                        location: '',
                        category: '',
                        contactPerson: '',
                        email: '',
                        phone: '',
                        address: '',
                        pan: '',
                        aadhar: '',
                        tan: '',
                        gst: '',
                        bankName: '',
                        acNo: '',
                        vendorCode: '',
                        poNumber: '',
                        invoiceDate: '',
                        remarks: '',
                        contractStart: '',
                        contractEnd: '',
                        paymentTerms: 'Monthly',
                        certifications: []
                      });
                    }}
                    className="p-2 hover:bg-white/20 rounded-lg transition-colors"
                  >
                    <X className="w-5 h-5 text-white" />
                  </button>
                </div>

                {/* Error Banner - High Visibility Placement */}
                {modalError && (
                  <div style={{
                    margin: '1rem 1.5rem 0',
                    padding: '0.75rem 1rem',
                    backgroundColor: '#fee2e2',
                    border: '1px solid #fecaca',
                    borderRadius: '0.75rem',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.75rem',
                    color: '#b91c1c',
                    zIndex: 9999
                  }}>
                    <AlertCircle style={{ width: '1.25rem', height: '1.25rem', flexShrink: 0 }} />
                    <div style={{ flex: 1 }}>
                      <p style={{ fontSize: '0.875rem', fontWeight: 'bold', margin: 0 }}>{modalError}</p>
                    </div>
                    <button
                      onClick={() => setModalError('')}
                      style={{
                        padding: '0.25rem',
                        cursor: 'pointer',
                        borderRadius: '9999px',
                        border: 'none',
                        background: 'transparent',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}
                    >
                      <X style={{ width: '1rem', height: '1rem', color: '#ef4444' }} />
                    </button>
                  </div>
                )}



                {/* Modal Body */}
                <div className="flex-1 overflow-y-auto p-6 bg-slate-50/50">
                  <div className="space-y-6">
                    {/* Basic Info */}
                    <div className="bg-white p-5 rounded-xl border border-slate-200 shadow-sm">
                      <div className="flex items-center gap-2 mb-4 pb-2 border-b border-slate-100">
                        <h4 className="text-sm vendor-popup-header mb-0 text-center flex-1">Basic Information</h4>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div className="md:col-span-2">
                          <label className="block text-xs mb-1.5 vendor-popup-label">
                            Company Name <span className="text-red-500">*</span>
                          </label>
                          <input
                            type="text"
                            value={newVendor.name}
                            onChange={(e) => {
                              setNewVendor({ ...newVendor, name: e.target.value });
                              if (e.target.value && formErrors.name) {
                                setFormErrors(prev => ({ ...prev, name: false }));
                              }
                            }}
                            className={`w-full px-3 py-2 border ${formErrors.name ? 'border-red-500 bg-red-50' : 'border-slate-300'} rounded-lg focus:ring-2 focus:ring-[#0084CF] focus:border-[#0084CF] text-sm vendor-popup-input`}
                            placeholder="e.g. Acme Solar Solutions Pvt Ltd"
                            required
                          />
                        </div>
                        <div>
                          <label className="block text-xs mb-1.5 vendor-popup-label">
                            Category <span className="text-red-500">*</span>
                          </label>
                          <select
                            value={newVendor.category}
                            onChange={(e) => {
                              setNewVendor({ ...newVendor, category: e.target.value });
                              if (e.target.value && formErrors.category) {
                                setFormErrors(prev => ({ ...prev, category: false }));
                              }
                            }}
                            className={`w-full px-3 py-2 border ${formErrors.category ? 'border-red-500 bg-red-50' : 'border-slate-300'} rounded-lg focus:ring-2 focus:ring-[#0084CF] focus:border-[#0084CF] text-sm bg-white vendor-popup-input`}
                          >
                            <option value="">Select Category</option>
                            {categories.map(cat => <option key={cat} value={cat}>{cat}</option>)}
                          </select>
                        </div>
                        <div>
                          <label className="block text-xs mb-1.5 vendor-popup-label">
                            Location
                          </label>
                          <div className="relative">
                            <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                            <input
                              type="text"
                              value={newVendor.location}
                              onChange={(e) => setNewVendor({ ...newVendor, location: e.target.value })}
                              className="w-full pl-9 pr-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-[#0084CF] focus:border-[#0084CF] text-sm vendor-popup-input"
                              placeholder="City, State"
                            />
                          </div>
                        </div>
                        <div>
                          <label className="block text-xs mb-1.5 vendor-popup-label">
                            PAN
                          </label>
                          <input
                            type="text"
                            value={newVendor.pan}
                            onChange={(e) => setNewVendor({ ...newVendor, pan: e.target.value })}
                            className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-[#0084CF] focus:border-[#0084CF] text-sm font-mono uppercase vendor-popup-input"
                            placeholder="XXXXX0000X"
                          />
                        </div>
                        <div>
                          <label className="block text-xs mb-1.5 vendor-popup-label">
                            Aadhar
                          </label>
                          <input
                            type="text"
                            value={newVendor.aadhar}
                            onChange={(e) => setNewVendor({ ...newVendor, aadhar: e.target.value })}
                            className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-[#0084CF] focus:border-[#0084CF] text-sm font-mono vendor-popup-input"
                            placeholder="0000 0000 0000"
                          />
                        </div>
                        <div>
                          <label className="block text-xs mb-1.5 vendor-popup-label">
                            TAN
                          </label>
                          <input
                            type="text"
                            value={newVendor.tan}
                            onChange={(e) => setNewVendor({ ...newVendor, tan: e.target.value })}
                            className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-[#0084CF] focus:border-[#0084CF] text-sm font-mono uppercase vendor-popup-input"
                            placeholder="XXXX00000X"
                          />
                        </div>
                        <div>
                          <label className="block text-xs mb-1.5 vendor-popup-label">
                            GSTIN
                          </label>
                          <input
                            type="text"
                            value={newVendor.gst}
                            onChange={(e) => setNewVendor({ ...newVendor, gst: e.target.value })}
                            className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-[#0084CF] focus:border-[#0084CF] text-sm font-mono uppercase vendor-popup-input"
                            placeholder="27XXXXX0000X1Z5"
                          />
                        </div>
                      </div>
                    </div>

                    {/* Contact Info */}
                    <div className="bg-white p-5 rounded-xl border border-slate-200 shadow-sm">
                      <div className="flex items-center gap-2 mb-4 pb-2 border-b border-slate-100">
                        <h4 className="text-sm vendor-popup-header mb-0 text-center flex-1">Contact Details</h4>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div>
                          <label className="block text-xs mb-1.5 vendor-popup-label">
                            Contact Person <span className="text-red-500">*</span>
                          </label>
                          <input
                            type="text"
                            value={newVendor.contactPerson}
                            onChange={(e) => {
                              setNewVendor({ ...newVendor, contactPerson: e.target.value });
                              if (e.target.value && formErrors.contactPerson) {
                                setFormErrors(prev => ({ ...prev, contactPerson: false }));
                              }
                            }}
                            className={`w-full px-3 py-2 border ${formErrors.contactPerson ? 'border-red-500 bg-red-50' : 'border-slate-300'} rounded-lg focus:ring-2 focus:ring-[#0084CF] focus:border-[#0084CF] text-sm vendor-popup-input`}
                            placeholder="Full Name"
                          />
                        </div>
                        <div>
                          <label className="block text-xs mb-1.5 vendor-popup-label">
                            Email
                          </label>
                          <div className="relative">
                            <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                            <input
                              type="email"
                              value={newVendor.email}
                              onChange={(e) => setNewVendor({ ...newVendor, email: e.target.value })}
                              className="w-full pl-9 pr-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-[#0084CF] focus:border-[#0084CF] text-sm vendor-popup-input"
                              placeholder="email@company.com"
                            />
                          </div>
                        </div>
                        <div>
                          <label className="block text-xs mb-1.5 vendor-popup-label">
                            Phone
                          </label>
                          <div className="relative">
                            <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                            <input
                              type="tel"
                              value={newVendor.phone}
                              onChange={(e) => setNewVendor({ ...newVendor, phone: e.target.value })}
                              className="w-full pl-9 pr-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-[#0084CF] focus:border-[#0084CF] text-sm vendor-popup-input"
                              placeholder="+91 98765 43210"
                            />
                          </div>
                        </div>
                        <div className="md:col-span-3">
                          <label className="block text-xs mb-1.5 vendor-popup-label">
                            Full Address
                          </label>
                          <textarea
                            value={newVendor.address}
                            onChange={(e) => setNewVendor({ ...newVendor, address: e.target.value })}
                            rows="2"
                            className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-[#0084CF] focus:border-[#0084CF] text-sm resize-none vendor-popup-input"
                            placeholder="Registered office address..."
                          />
                        </div>
                      </div>
                    </div>

                    {/* Financial/Contract Info */}
                    <div className="bg-white p-5 rounded-xl border border-slate-200 shadow-sm">
                      <div className="flex items-center gap-2 mb-4 pb-2 border-b border-slate-100">
                        <h4 className="text-sm vendor-popup-header mb-0 text-center flex-1">Financial & Contract</h4>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div>
                          <label className="block text-xs mb-1.5 vendor-popup-label">
                            Bank Name
                          </label>
                          <input
                            type="text"
                            value={newVendor.bankName}
                            onChange={(e) => setNewVendor({ ...newVendor, bankName: e.target.value })}
                            className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-[#0084CF] focus:border-[#0084CF] text-sm vendor-popup-input"
                          />
                        </div>
                        <div>
                          <label className="block text-xs mb-1.5 vendor-popup-label">
                            Account Number
                          </label>
                          <input
                            type="text"
                            value={newVendor.acNo}
                            onChange={(e) => setNewVendor({ ...newVendor, acNo: e.target.value })}
                            className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-[#0084CF] focus:border-[#0084CF] text-sm font-mono vendor-popup-input"
                          />
                        </div>
                        <div>
                          <label className="block text-xs mb-1.5 vendor-popup-label">
                            Vendor Code / ID
                          </label>
                          <input
                            type="text"
                            value={newVendor.vendorCode}
                            onChange={(e) => setNewVendor({ ...newVendor, vendorCode: e.target.value })}
                            className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-[#0084CF] focus:border-[#0084CF] text-sm vendor-popup-input"
                          />
                        </div>
                        <div>
                          <label className="block text-xs mb-1.5 vendor-popup-label">
                            PO Number
                          </label>
                          <input
                            type="text"
                            value={newVendor.poNumber}
                            onChange={(e) => setNewVendor({ ...newVendor, poNumber: e.target.value })}
                            className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-[#0084CF] focus:border-[#0084CF] text-sm vendor-popup-input"
                          />
                        </div>
                        <div>
                          <label className="block text-xs mb-1.5 vendor-popup-label">
                            PO Value
                          </label>
                          <input
                            type="number"
                            value={newVendor.poValue}
                            onChange={(e) => setNewVendor({ ...newVendor, poValue: e.target.value })}
                            className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-[#0084CF] focus:border-[#0084CF] text-sm vendor-popup-input"
                          />
                        </div>
                        <div>
                          <label className="block text-xs mb-1.5 vendor-popup-label">
                            Contract Start
                          </label>
                          <input
                            type="date"
                            value={newVendor.contractStart}
                            onChange={(e) => setNewVendor({ ...newVendor, contractStart: e.target.value })}
                            className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-[#0084CF] focus:border-[#0084CF] text-sm vendor-popup-input"
                          />
                        </div>
                        <div>
                          <label className="block text-xs mb-1.5 vendor-popup-label">
                            Contract End
                          </label>
                          <input
                            type="date"
                            value={newVendor.contractEnd}
                            onChange={(e) => setNewVendor({ ...newVendor, contractEnd: e.target.value })}
                            className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-[#0084CF] focus:border-[#0084CF] text-sm vendor-popup-input"
                          />
                        </div>
                        <div>
                        </div>
                        <div>
                          <label className="block text-xs mb-1.5 vendor-popup-label">
                            Payment Schedule
                          </label>
                          <select
                            value={newVendor.paymentTerms}
                            onChange={(e) => setNewVendor({ ...newVendor, paymentTerms: e.target.value })}
                            className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-[#0084CF] focus:border-[#0084CF] text-sm bg-white vendor-popup-input"
                          >
                            <option value="">Select Schedule</option>
                            {paymentTerms.map(term => (
                              <option key={term} value={term}>{term}</option>
                            ))}
                          </select>
                        </div>
                        <div>
                          <label className="block text-xs mb-1.5 vendor-popup-label">
                            Invoice Date
                          </label>
                          <input
                            type="date"
                            value={newVendor.invoiceDate}
                            onChange={(e) => setNewVendor({ ...newVendor, invoiceDate: e.target.value })}
                            className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-[#0084CF] focus:border-[#0084CF] text-sm vendor-popup-input"
                          />
                        </div>
                        <div>
                          <label className="block text-xs mb-1.5 vendor-popup-label">
                            Invoice Number
                          </label>
                          <input
                            type="text"
                            value={newVendor.invoiceNumber}
                            onChange={(e) => setNewVendor({ ...newVendor, invoiceNumber: e.target.value })}
                            className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-[#0084CF] focus:border-[#0084CF] text-sm vendor-popup-input"
                          />
                        </div>
                        <div>
                          <label className="block text-xs mb-1.5 vendor-popup-label">
                            Invoice Value
                          </label>
                          <input
                            type="number"
                            value={newVendor.invoiceValue}
                            onChange={(e) => {
                              const val = parseFloat(e.target.value) || 0;
                              const gst = parseFloat(newVendor.gstAmount) || 0;
                              setNewVendor({
                                ...newVendor,
                                invoiceValue: e.target.value,
                                totalInvoiceValue: (val + gst).toFixed(2)
                              });
                            }}
                            className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-[#0084CF] focus:border-[#0084CF] text-sm vendor-popup-input"
                          />
                        </div>
                        <div>
                          <label className="block text-xs mb-1.5 vendor-popup-label">
                            GST Amount
                          </label>
                          <input
                            type="number"
                            value={newVendor.gstAmount}
                            onChange={(e) => {
                              const gst = parseFloat(e.target.value) || 0;
                              const val = parseFloat(newVendor.invoiceValue) || 0;
                              setNewVendor({
                                ...newVendor,
                                gstAmount: e.target.value,
                                totalInvoiceValue: (val + gst).toFixed(2)
                              });
                            }}
                            className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-[#0084CF] focus:border-[#0084CF] text-sm vendor-popup-input"
                          />
                        </div>
                        <div>
                          <label className="block text-xs mb-1.5 vendor-popup-label">
                            Total Invoice Value
                          </label>
                          <input
                            type="number"
                            value={newVendor.totalInvoiceValue}
                            onChange={(e) => setNewVendor({ ...newVendor, totalInvoiceValue: e.target.value })}
                            className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-[#0084CF] focus:border-[#0084CF] text-sm bg-gray-50 vendor-popup-input"
                            readOnly
                          />
                        </div>
                        <div>
                          <label className="block text-xs mb-1.5 vendor-popup-label">
                            Invoice Document
                          </label>
                          <div className="relative border border-slate-300 rounded-lg overflow-hidden bg-white">
                            <input
                              type="file"
                              accept=".pdf, .jpg, .png"
                              className="w-full px-3 py-2 opacity-0 absolute inset-0 cursor-pointer z-10"
                              onChange={(e) => {
                                if (e.target.files[0]) {
                                  setNewVendor({ ...newVendor, invoiceDocument: e.target.files[0] });
                                }
                              }}
                            />
                            <div className="flex items-center justify-center gap-2 px-3 py-2">
                              <span className="text-sm text-gray-500 truncate">{newVendor.invoiceDocument ? newVendor.invoiceDocument.name : 'Choose file...'}</span>
                              <Upload className="w-4 h-4 text-gray-400" />
                            </div>
                          </div>
                        </div>
                        <div className="md:col-span-3">
                          <label className="block text-xs mb-1.5 vendor-popup-label">
                            Remarks / Notes
                          </label>
                          <textarea
                            value={newVendor.remarks}
                            onChange={(e) => setNewVendor({ ...newVendor, remarks: e.target.value })}
                            rows="2"
                            className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-[#0084CF] focus:border-[#0084CF] text-sm resize-none vendor-popup-input"
                            placeholder="Additional details..."
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Modal Footer */}
                <div className="bg-gray-50 px-6 py-4 flex items-center justify-center gap-3 border-t border-gray-200 shrink-0">
                  <button
                    onClick={() => {
                      setFormErrors({});
                      setModalError('');
                      setShowAddModal(false);
                      setEditingVendor(null);
                      setNewVendor({
                        name: '',
                        location: '',
                        category: '',
                        contactPerson: '',
                        email: '',
                        phone: '',
                        address: '',
                        pan: '',
                        aadhar: '',
                        tan: '',
                        gst: '',
                        bankName: '',
                        acNo: '',
                        vendorCode: '',
                        poNumber: '',
                        poValue: '',
                        invoiceDate: '',
                        invoiceNumber: '',
                        invoiceValue: '',
                        gstAmount: '',
                        totalInvoiceValue: '',
                        invoiceDocument: null,
                        remarks: '',
                        contractStart: '',
                        contractEnd: '',
                        paymentTerms: 'Monthly',
                        certifications: []
                      });
                    }}
                    className="flex-1 px-5 py-2.5 border-2 border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100 transition-colors font-medium"
                  >
                    Cancel
                  </button>
                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={editingVendor ? handleUpdateVendor : handleAddVendor}
                    className="flex-1 px-5 py-2.5 bg-gradient-to-r from-[#0066A3] to-[#0066A3] text-white rounded-lg hover:from-[#1C4D88] hover:to-[#1C4D88] transition-all font-semibold shadow-md"
                  >
                    {editingVendor ? 'Update Vendor' : 'Add Vendor'}
                  </motion.button>
                </div>
              </motion.div>
            </div>
          )}
        </AnimatePresence>

        {/* Upload Selection Modal */}
        <AnimatePresence>
          {showUploadModal && (
            <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center z-[10010] p-4">
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.95 }}
                className="bg-white rounded-2xl border border-slate-100 shadow-2xl w-full max-w-md overflow-hidden"
              >
                <div className="p-6">
                  <div className="flex items-center justify-between mb-6">
                    <h3 className="text-xl font-bold text-gray-900">Select Bulk Import Type</h3>
                    <button
                      onClick={() => setShowUploadModal(false)}
                      className="p-2 hover:bg-gray-100 rounded-full transition-colors"
                    >
                      <X className="w-5 h-5 text-gray-500" />
                    </button>
                  </div>

                  <div className="space-y-4">
                    <button
                      onClick={() => fileInputRef.current?.click()}
                      className="w-full flex items-center gap-4 p-4 border border-[#C2DFF1] rounded-xl hover:bg-[#F2F8FC] transition-colors group text-left"
                    >
                      <div className="p-3 bg-[#E3F0F9] text-[#0066A3] rounded-lg group-hover:bg-[#C2DFF1] transition-colors">
                        <Users className="w-6 h-6" />
                      </div>
                      <div>
                        <div className="force-normal-black text-lg">Vendor Master Data</div>
                        <p className="text-sm force-normal-black">Upload new vendors from Excel/CSV</p>
                      </div>
                    </button>

                    <button
                      onClick={() => {
                        setShowUploadModal(false);
                        setSelectedVendorForInvoice(null); // Ensure vendor selection is required
                        setNewInvoice({
                          invoiceDate: '',
                          invoiceNumber: '',
                          invoiceValue: '',
                          gstAmount: '',
                          totalInvoiceValue: '',
                          poNumber: '',
                          invoiceDocument: null
                        });
                        setShowInvoiceModal(true);
                      }}
                      className="w-full flex items-center gap-4 p-4 border border-emerald-200 rounded-xl hover:bg-emerald-50 transition-colors group text-left"
                    >
                      <div className="p-3 bg-emerald-100 text-emerald-600 rounded-lg group-hover:bg-emerald-200 transition-colors">
                        <FileText className="w-6 h-6" />
                      </div>
                      <div>
                        <div className="force-normal-black text-lg">Add Invoice Record</div>
                        <p className="text-sm force-normal-black">Manually add a new invoice record</p>
                      </div>
                    </button>
                  </div>
                </div>
                <div className="p-4 bg-gray-50 border-t border-gray-100 flex justify-end">
                  <button
                    onClick={() => setShowUploadModal(false)}
                    className="px-4 py-2 bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 font-medium text-sm"
                  >
                    Cancel
                  </button>
                </div>
              </motion.div>
            </div>
          )}
        </AnimatePresence>

        {/* Add Invoice Modal */}
        <AnimatePresence>
          {showInvoiceModal && (
            <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center z-[10020] p-4">
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.95 }}
                className="bg-white rounded-2xl border border-slate-100 shadow-2xl w-full max-w-2xl overflow-hidden"
              >
                <form onSubmit={handleAddInvoice}>
                  <div className="bg-gradient-to-r from-[#0066A3] to-[#0066A3] px-6 py-4 flex items-center justify-between shrink-0">
                    <div className="flex-1 text-center">
                      <h3 className="text-xl font-[800] text-white">Add New Invoice</h3>
                      <p className="text-[#E3F0F9] text-sm mt-0.5">
                        {selectedVendorForInvoice ? `Add invoice for ${selectedVendorForInvoice.name}` : 'Create a new invoice record'}
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={() => setShowInvoiceModal(false)}
                      className="p-2 hover:bg-white/20 rounded-lg transition-colors"
                    >
                      <X className="w-5 h-5 text-white" />
                    </button>
                  </div>

                  <div className="p-6 space-y-4">
                    {/* Select Vendor if not already selected */}
                    {!selectedVendorForInvoice && (
                      <div className="bg-orange-50 p-4 rounded-xl border border-orange-100 mb-4">
                        <label className="block text-sm font-bold text-gray-700 mb-2">Select Vendor <span className="text-red-500">*</span></label>
                        <div className="relative">
                          <select
                            required
                            className="w-full pl-3 pr-10 py-2.5 border border-orange-200 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 bg-white"
                            onChange={(e) => {
                              const v = filteredVendors.find(vend => vend.id === parseInt(e.target.value));
                              setSelectedVendorForInvoice(v);
                              if (v) setNewInvoice(prev => ({ ...prev, poNumber: v.po_number || '' }));
                            }}
                            defaultValue=""
                          >
                            <option value="" disabled>Select a vendor from the list...</option>
                            {filteredVendors.map(v => (
                              <option key={v.id} value={v.id}>{v.name} ({v.vendor_code || 'No Code'})</option>
                            ))}
                          </select>
                          <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-500 pointer-events-none" />
                        </div>
                      </div>
                    )}

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-[800] text-gray-700 mb-1">Invoice Number</label>
                        <input
                          type="text"
                          required
                          value={newInvoice.invoiceNumber}
                          onChange={(e) => setNewInvoice({ ...newInvoice, invoiceNumber: e.target.value })}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0084CF] focus:border-[#0084CF]"
                          placeholder="e.g. INV-2024-001"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-[800] text-gray-700 mb-1">Invoice Date</label>
                        <input
                          type="date"
                          required
                          value={newInvoice.invoiceDate}
                          onChange={(e) => setNewInvoice({ ...newInvoice, invoiceDate: e.target.value })}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0084CF] focus:border-[#0084CF]"
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-3 gap-4">
                      <div>
                        <label className="block text-sm font-[800] text-gray-700 mb-1">Invoice Value</label>
                        <div className="relative">
                          <span className="absolute left-3 top-2 text-gray-400">₹</span>
                          <input
                            type="number"
                            required
                            value={newInvoice.invoiceValue}
                            onChange={(e) => {
                              const val = parseFloat(e.target.value) || 0;
                              const gst = parseFloat(newInvoice.gstAmount) || 0;
                              setNewInvoice({
                                ...newInvoice,
                                invoiceValue: e.target.value,
                                totalInvoiceValue: (val + gst).toFixed(2)
                              });
                            }}
                            className="w-full pl-8 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0084CF] focus:border-[#0084CF]"
                            placeholder="0.00"
                          />
                        </div>
                      </div>
                      <div>
                        <label className="block text-sm font-[800] text-gray-700 mb-1">GST Amount</label>
                        <div className="relative">
                          <span className="absolute left-3 top-2 text-gray-400">₹</span>
                          <input
                            type="number"
                            required
                            value={newInvoice.gstAmount}
                            onChange={(e) => {
                              const gst = parseFloat(e.target.value) || 0;
                              const val = parseFloat(newInvoice.invoiceValue) || 0;
                              setNewInvoice({
                                ...newInvoice,
                                gstAmount: e.target.value,
                                totalInvoiceValue: (val + gst).toFixed(2)
                              });
                            }}
                            className="w-full pl-8 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0084CF] focus:border-[#0084CF]"
                            placeholder="0.00"
                          />
                        </div>
                      </div>
                      <div>
                        <label className="block text-sm font-[800] text-gray-700 mb-1">Total Value</label>
                        <div className="relative">
                          <span className="absolute left-3 top-2 text-gray-400">₹</span>
                          <input
                            type="number"
                            readOnly
                            value={newInvoice.totalInvoiceValue}
                            className="w-full pl-8 pr-3 py-2 border border-gray-300 rounded-lg bg-gray-50 font-[800]"
                          />
                        </div>
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-[800] text-gray-700 mb-1">PO Number</label>
                        <input
                          type="text"
                          value={newInvoice.poNumber}
                          onChange={(e) => setNewInvoice({ ...newInvoice, poNumber: e.target.value })}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0084CF] focus:border-[#0084CF]"
                          placeholder="PO Number"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-[800] text-gray-700 mb-1">Invoice Document</label>
                        <div className="relative">
                          <input
                            type="file"
                            accept=".pdf"
                            onChange={(e) => setNewInvoice({ ...newInvoice, invoiceDocument: e.target.files[0] })}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0084CF] focus:border-[#0084CF] text-sm file:mr-4 file:py-1 file:px-2 file:rounded-full file:border-0 file:text-xs file:font-semibold file:bg-[#F2F8FC] file:text-[#1C4D88] hover:file:bg-[#E3F0F9]"
                          />
                          <FileText className="absolute right-3 top-2.5 w-4 h-4 text-gray-400" />
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="p-4 bg-gray-50 border-t border-gray-100 flex justify-center gap-3 shrink-0">
                    <button
                      type="button"
                      onClick={() => setShowInvoiceModal(false)}
                      className="px-4 py-2 bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 font-medium text-sm"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      disabled={!selectedVendorForInvoice}
                      className="px-6 py-2 bg-[#0066A3] text-white rounded-lg hover:bg-[#1C4D88] font-medium text-sm shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Save Invoice
                    </button>
                  </div>
                </form>
              </motion.div>
            </div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};

export default VendorPage;

